import { queryOptions } from '@tanstack/react-query'
import { apiFetch } from '#/api/client'

// What Annwn holds or cites beside its concepts (GET /corpus), the same for
// every account. Every code below mirrors a Literal in
// `annwn_shared.corpus.vocabulary` and is titled through `corpus.*` i18n keys;
// a code the platform adds before the frontend has copy for it shows readably
// (see `useCorpusLabels`). The list serves real holdings first, then samples.

export type CorpusTheme =
  | 'carbon'
  | 'water'
  | 'supply_chain'
  | 'reporting_disclosure'
  | 'regulatory_claims'
  | 'assurance_controls'

type CorpusKind = 'framework' | 'dataset'
export type Provenance = 'authoritative' | 'derived'
export type CorpusStatus = 'draft' | 'in_use'
type Holding = 'held' | 'cited'

// What may be done with a holding's content;
// `unknown` means nobody has checked the publisher's terms.
type Licence = 'open_attribution' | 'licensed' | 'unknown'

export type CorpusItem = {
  id: string
  kind: CorpusKind
  name: string
  summary: string
  theme: CorpusTheme
  provenance: Provenance
  status: CorpusStatus
  version: string | null
  publisher: string
  licence: Licence
  // The credit the licence requires, shown wherever the content is.
  attribution: string | null
  // Where the work is published.
  url: string | null
  holding: Holding
  cited_by: Array<string>
  // A demonstration entry rather than a real holding; marked as one wherever
  // it shows.
  sample: boolean
}

type CorpusItemListResponse = { data: Array<CorpusItem> }

const corpusKeys = {
  all: ['corpus'] as const,
}

// The catalogue only changes with a platform release.
const CATALOGUE_STALE_TIME = 30 * 60 * 1000

export const corpusItemsQueryOptions = queryOptions({
  queryKey: corpusKeys.all,
  queryFn: async ({ signal }) => {
    const res = await apiFetch<CorpusItemListResponse>({
      path: '/corpus',
      signal,
    })
    return res.data
  },
  staleTime: CATALOGUE_STALE_TIME,
})
